import { initDataGridDragReorder } from '../manager/data-grid-drag-reorder.js';
import { setDurationControlOngoingAvailable } from '../shared/duration-control.js';
import { setInstallmentAmountEditable } from '../manager/pledges/pledge-edit-ongoing-modal.js';
import {
    applyCountInputFromField,
    applyRemainingInputFromField,
    applySimpleRecurringPlan,
    clearScheduleEditTarget,
    isFinitePledgeRecurringEdit,
    setManualCardCollapsed,
    syncInstallmentWidget,
    syncOngoingEditFromNumLeft,
    updateEditRecurringModePanel,
} from '../shared/ongoing-pledge-edit-ui.js';
import {
    formatCurrency,
    getPaymentMode,
    isPledgePayRecurringDurationReady,
    pledgePayBodyFromShell,
    pledgePayUiRoot,
} from './pledge-pay-utils';
import { getSelectedPledgeSurchargeTotal, getSurchargePrincipalTotals } from './pledge-pay-surcharge';
import { lastInstallmentAmount, money } from '../shared/recurring-edit-plan.js';
import { pledgeScheduleStartDatepickerWidget } from '../shared/pledge-schedule-start-date.js';
let recurringScheduleData = null;

export function setRecurringScheduleData(data) {
    recurringScheduleData = data;
}

function addMonthsNoOverflow(date, months) {
    const dayOfMonth = date.getDate();

    date.setDate(1);
    date.setMonth(date.getMonth() + months);

    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(dayOfMonth, lastDayOfMonth));
}

function getPledgeStartDateFromFrequency(frequency) {
    if (!frequency) return null;

    const dayOffsets = { daily: 1, weekly: 7, biweekly: 14 };
    const monthOffsets = { monthly: 1, bimonthly: 2, quarterly: 3, biannually: 6, annually: 12 };

    const date = new Date();
    if (Object.hasOwn(dayOffsets, frequency)) {
        date.setDate(date.getDate() + dayOffsets[frequency]);
    } else if (Object.hasOwn(monthOffsets, frequency)) {
        addMonthsNoOverflow(date, monthOffsets[frequency]);
    } else {
        return null;
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${month}/${day}/${date.getFullYear()}`;
}

function setPledgeStartDateByFrequency($container, frequency) {
    const newDate = getPledgeStartDateFromFrequency(frequency);
    if (!newDate) return;

    const $input = $container.find('input[name="pledge_start_date"]');
    if ($input.length) {
        $input.val(newDate);
    }

    const $datepicker = pledgeScheduleStartDatepickerWidget($container);
    if ($datepicker.length && $datepicker.datepicker) {
        $datepicker.datepicker("setDate", newDate);
    }
}

function resolvePledgePayBody($root) {
    if ($root && $root.length) {
        return pledgePayBodyFromShell(pledgePayUiRoot($root));
    }
    const $body = $('#pledge_pay_body').first();
    return $body.length ? $body : null;
}

function setRecurringNoteVisibility($el, visible) {
    if (!$el || !$el.length) {
        return;
    }
    $el.toggleClass('pledge-pay-hidden', !visible);
    if (visible) {
        $el.show();
    } else {
        $el.hide();
    }
}

function resolveRecurringPaymentPrincipal($parent, totals) {
    if (totals.total > 0) {
        return totals.total;
    }
    const fromHiddenTotal = parseFloat($parent.find('#total_amount').val());
    if (Number.isFinite(fromHiddenTotal) && fromHiddenTotal > 0) {
        return fromHiddenTotal;
    }
    return 0;
}

function calculateRecurringPaymentAmount($root) {
    const round2 = n => Math.round(n * 100) / 100;
    const $parent = resolvePledgePayBody($root);
    if (!$parent || !$parent.length) {
        return;
    }

    const $freq = $parent.find('select[name="frequency"]');
    const frequency = $freq.val();
    const duration = parseInt($parent.find('#pledge_payment_duration').val(), 10) || 1;
    const isRecurringMode = $parent.find('input[name="payment_mode"]:checked').val() === 'recurring';

    const totals = getSurchargePrincipalTotals($parent);
    const totalPrincipal = resolveRecurringPaymentPrincipal($parent, totals);

    if (!isRecurringMode || !frequency || duration < 2 || !totalPrincipal) {
        setRecurringNoteVisibility($parent.find('#recurring-payment-display'), false);
        setRecurringNoteVisibility($parent.find('#last-payment-note'), false);
        $parent.find('#recurring-payment-display').closest('.recurring-setup').addClass('pledge-pay-hidden').hide();
        $parent.find('input[name="installment_amount"], input[name="surcharge_installment_amount"]').val('');
        return;
    }

    const totalSurcharge = getSelectedPledgeSurchargeTotal($parent);
    const totalCombined = round2(totalPrincipal + totalSurcharge);

    const principalPerInstallment = round2(totalPrincipal / duration);
    const surchargePerInstallment = round2(totalSurcharge / duration);
    const installmentTotal = round2(principalPerInstallment + surchargePerInstallment);

    $parent.find('input[name="installment_amount"]').val(principalPerInstallment);
    $parent.find('input[name="surcharge_installment_amount"]').val(surchargePerInstallment);

    $parent.find('#recurring-payment-text').text(
        `${$freq.find('option:selected').text().trim()} Payment: $${formatCurrency(installmentTotal)}`
    );
    $parent.find('#recurring-payment-display').closest('.recurring-setup').removeClass('pledge-pay-hidden').show();
    setRecurringNoteVisibility($parent.find('#recurring-payment-display'), true);
    syncLastPaymentNote($parent.find('#last-payment-note'), totalCombined, installmentTotal, duration);
}

function syncLastPaymentNote($note, totalCombined, installmentTotal, duration) {
    if (!$note || !$note.length) {
        return;
    }
    const last = lastInstallmentAmount(totalCombined, installmentTotal, duration);
    const per = money(installmentTotal);
    if (last <= 0 || money(last) === per || per <= 0 || (parseInt(duration, 10) || 0) < 2) {
        setRecurringNoteVisibility($note, false);
        $note.text('');
        return;
    }
    setRecurringNoteVisibility($note, true);
    $note.text(`last payment will be $${formatCurrency(last)}`);
}

function syncInstallmentLastPaymentNote($modal) {
    if (!$modal || !$modal.length) {
        return;
    }
    const pledgeAmount = getRecurringEditModalPledgeAmount($modal);
    const total = getRecurringBalanceDueTotalForInstallment($modal, pledgeAmount);
    const per = parseFloat(String($modal.find('#on_amount').val() || '').replace(/,/g, '')) || 0;
    const dur = parseInt($modal.find('#on_num_left').val() || 0, 10) || 0;
    syncLastPaymentNote($modal.find('#installment_last_payment_note'), total, per, dur);
}

function getRecurringSchedulePledges() {
    return recurringScheduleData?.recurring_schedule?.pledges ?? [];
}

function getRecurringBalanceDueTotal() {
    return getRecurringSchedulePledges().reduce((total, pledge) => {
        const balance = parseFloat(pledge?.balance_due ?? 0);
        return total + (Number.isNaN(balance) ? 0 : balance);
    }, 0);
}

function recurringPledgeIdsMatch(leftId, rightId) {
    return leftId != null && rightId != null && String(leftId) === String(rightId);
}

function getRecurringEditModalPledgeAmount($modal) {
    const $pledgeAmount = $modal.find('#pledge_amount_input');
    if (!$pledgeAmount.length || $modal.find('#pledge_totals_row').hasClass('d-none')) {
        return null;
    }
    const parsed = parseFloat(String($pledgeAmount.val() || '').replace(/,/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
}

function getRecurringBalanceDueTotalForInstallment($modal, pledgeAmount = null) {
    const pledges = getRecurringSchedulePledges();
    const currentPledgeId = recurringScheduleData?.id ?? null;

    return pledges.reduce((total, pledge) => {
        const pledgeId = pledge.id ?? pledge.pledge_id ?? null;
        let balance;

        if (pledgeAmount != null && recurringPledgeIdsMatch(pledgeId, currentPledgeId)) {
            const amountPaid = parseFloat($modal.find('#pledge_amount_input').data('amountPaid')) || 0;
            balance = Math.max(0, pledgeAmount - amountPaid);
        } else {
            balance = parseFloat(pledge?.balance_due ?? 0);
        }
        return total + (Number.isNaN(balance) ? 0 : balance);
    }, 0);
}

function syncPledgeBalanceDueDisplay($modal) {
    const $amount = $modal.find('#pledge_amount_input');
    if (!$amount.length || $modal.find('#pledge_totals_row').hasClass('d-none')) {
        return;
    }
    const amountPaid = parseFloat($amount.data('amountPaid')) || 0;
    const amount = parseFloat(String($amount.val() || '').replace(/,/g, '')) || 0;
    $modal.find('#pledge_balance_due').text(formatCurrency(Math.max(0, amount - amountPaid)));
}

function getRecurringPledgeCount() {
    return getRecurringSchedulePledges().length;
}

function parseModalInstallment($modal) {
    const parsed = parseFloat(String($modal.find('#on_amount').val() || '').replace(/,/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function storedInstallmentAmount(record) {
    return parseFloat(
        record?.recurring_schedule?.installment_amount
        ?? record?.installment_amount
        ?? record?.amount
        ?? 0
    ) || 0;
}

function keepCurrentInstallment($modal, record) {
    return parseModalInstallment($modal) || storedInstallmentAmount(record);
}

function getInstallmentAmount($modal, numLeft, pledgeAmount = null) {
    const record = recurringScheduleData;
    if (!record) {
        return 0;
    }

    if (record.ongoing_indefinitely) {
        return keepCurrentInstallment($modal, record);
    }

    const remainingPayments = parseInt(numLeft ?? record?.num_left ?? 0, 10);
    if (!remainingPayments) {
        return keepCurrentInstallment($modal, record);
    }

    const totalBalanceDue = getRecurringBalanceDueTotalForInstallment($modal, pledgeAmount);
    if (!(totalBalanceDue > 0)) {
        return keepCurrentInstallment($modal, record);
    }
    return parseFloat((totalBalanceDue / remainingPayments).toFixed(2));
}

function setPledgeTotals($modal) {
    const record = recurringScheduleData;
    const totalAmount = formatCurrency(record?.amount ?? 0);
    const balanceDue = formatCurrency(record?.balance_due ?? 0);
    const amountPaid = formatCurrency(record?.amount_paid ?? record?.ongoing_amount_paid ?? 0);
    const $amountInput = $modal.find('#pledge_amount_input');
    const $amountGroup = $modal.find('#pledge_amount_input_group');
    const $balanceLine = $modal.find('#pledge_balance_due_line');

    $amountInput.val(totalAmount);
    $amountInput.data('initial', $amountInput.val());
    $amountInput.data('amountPaid', parseFloat(amountPaid) || 0);
    $modal.find('#pledge_amount_paid').text(amountPaid);
    $modal.find('#pledge_balance_due').text(balanceDue);

    const isOngoingIndefinitely = !!record?.ongoing_indefinitely;
    $modal.find('#pledge_totals_row').toggleClass('d-none', !record);
    $amountGroup.toggleClass('d-none', isOngoingIndefinitely);
    $balanceLine.toggleClass('d-none', isOngoingIndefinitely);
    $amountInput.prop('disabled', isOngoingIndefinitely);
}

function validatePledgeAmountInput($modal) {
    if ($modal.data('ongoingType') === 'Donation') {
        return true;
    }
    const $amount = $modal.find('#pledge_amount_input');
    const $error = $modal.find('#pledge_amount_error');

    if (!$amount.length || $modal.find('#pledge_totals_row').hasClass('d-none')) {
        $error.prop('hidden', true).text('');
        $amount.css('border-color', '');
        return true;
    }

    const amountPaid = parseFloat($amount.data('amountPaid')) || 0;
    const amount = parseFloat(($amount.val() || '').replace(/,/g, '')) || 0;

    const valid = amount >= amountPaid;

    $amount.css('border-color', valid ? '' : 'var(--danger)');
    $modal.find('#edit_recurring_remaining_input').css('border-color', valid ? '' : 'var(--danger)');
    $error.text(valid ? '' : 'Remaining cannot be below $0.')
        .prop('hidden', valid);

    return valid;
}

function setInstallmentAmountNote($modal) {
    const pledges = getRecurringSchedulePledges();
    const selectedId = recurringScheduleData?.id ?? null;
    const pledgeAmount = getRecurringEditModalPledgeAmount($modal);

    const otherPledgesWithBalance = pledges.filter(({id, pledge_id, balance_due}) => {
        const pledgeId = id ?? pledge_id ?? null;
        if (!pledgeId || recurringPledgeIdsMatch(pledgeId, selectedId)) {
            return false;
        }
        return (Number(balance_due) || 0) > 0;
    });

    const $note = $modal.find('#installment_amount_note');

    if (!otherPledgesWithBalance.length) {
        $note.addClass('d-none').text('');
        return;
    }

    const totalBalanceDue = formatCurrency(getRecurringBalanceDueTotalForInstallment($modal, pledgeAmount));

    $note
        .text(`This amount reflects $${totalBalanceDue} total due on ${getRecurringPledgeCount()} pledges linked to this recurring schedule.`)
        .removeClass('d-none');
}

function updateOngoingDurationOption($modal) {
    const durationField = $modal.find('#on_num_left').get(0);
    if (!durationField) {
        return;
    }

    const isOngoingIndefinitely = $modal.data('ongoingType') === 'Pledge'
        && recurringScheduleData?.ongoing_indefinitely;

    if ($modal.data('ongoingType') !== 'Pledge' || isOngoingIndefinitely) {
        setDurationControlOngoingAvailable(durationField, true);
        return;
    }

    setDurationControlOngoingAvailable(durationField, getRecurringPledgeCount() <= 1);
}

function updateRecurringInstallmentAmount($modal, pledge_type = 'Pledge') {
    if (!recurringScheduleData) {
        return;
    }
    if (pledge_type === 'Donation') {
        return;
    }
    if (isFinitePledgeRecurringEdit($modal)) {
        setInstallmentAmountNote($modal);
        syncInstallmentLastPaymentNote($modal);
        return;
    }
    const numLeft = $modal.find('#on_num_left').val();
    const pledgeAmount = getRecurringEditModalPledgeAmount($modal);
    const installmentAmount = getInstallmentAmount($modal, numLeft, pledgeAmount);
    $modal.find('#on_amount').val(formatCurrency(installmentAmount));
    setInstallmentAmountNote($modal);
    syncInstallmentLastPaymentNote($modal);
}

function closeEditOngoingPledgeModal() {
    const $modal = $('#PledgeEditOngoingModal');

    $modal.modal('hide');

    $modal.find([
        '#on_amount',
        '#on_num_left',
        '#on_orig_duration',
        '#on_frequency_id',
        '#on_campaign_id',
        '#ongoing_id',
        '#on_custom_desc',
        '#ongoing_user_id',
        '#ongoing_user_name',
        '#ongoing_user_phone',
        '#ongoing_user_email'
    ].join(',')).val('');

    $modal.find('#pledge_amount_input').val('').css('border-color', '');
    $modal.find('#pledge_amount_error').prop('hidden', true).text('');
    $modal.find('#pledge_totals_row').addClass('d-none');
    $modal.find('#installment_amount_note').addClass('d-none').text('');
    $modal.removeClass('is-cancelling');
    setManualCardCollapsed($modal, true);
    clearScheduleEditTarget($modal);

    setInstallmentAmountEditable($modal, true);

    recurringScheduleData = null;
    $modal.removeData('ongoingType');
}

function renumberRecurringPledgeOrders($tbody) {
    $tbody.find('tr').each(function (index) {
        $(this).find('.pledge-payment-order-num').text(index + 1);
    });
}

function initializeRecurringTable($root) {
    const $table = $root.find('#set_payment_table');
    const $tbody = $table.find('tbody');
    const tbody = $tbody[0];
    const rowCount = $tbody.find('tr').length;

    destroyRecurringTable($root);

    if (!$table.length || !$tbody.length || rowCount <= 1 || !tbody) {
        return;
    }

    initDataGridDragReorder({
        body: tbody,
        itemSelector: 'tr',
        handleSelector: '.pledge-payment-td-order',
        allowReinit: true,
        onReorder: function () {
            renumberRecurringPledgeOrders($tbody);
        },
    });

    $root.find('[data-toggle="tooltip"]').tooltip({ trigger: 'hover' });
}

function destroyRecurringTable($root) {
    const $tbody = $root.find('#set_payment_table tbody');
    const tbody = $tbody[0];
    if (!tbody) {
        return;
    }
    if (tbody._dataGridDragReorderAbort) {
        tbody._dataGridDragReorderAbort.abort();
        tbody._dataGridDragReorderAbort = null;
        tbody._dataGridDragReorderInit = false;
    }
    $tbody.find('tr').removeClass('sortable-hover');
}

function syncRecurringInstallmentFields($root) {
    const $body = resolvePledgePayBody($root);
    if (!$body || !$body.length) {
        return false;
    }
    if (getPaymentMode($body) !== 'recurring') {
        return true;
    }
    calculateRecurringPaymentAmount($root);
    const installment = parseFloat($body.find('input[name="installment_amount"]').val()) || 0;
    return isPledgePayRecurringDurationReady($body) && installment > 0;
}

$(document).on('change', '#pledge_pay_body select[name="frequency"]', function () {
    const $body = resolvePledgePayBody($(this));
    if ($body) {
        setPledgeStartDateByFrequency($body, $(this).val());
        calculateRecurringPaymentAmount($(this));
    }
});

$(document).on('focus', '#pledge_pay_body .pledge-select-wrap > select', function () {
    $(this).closest('.pledge-select-wrap').addClass('pledge-select-open');
});

$(document).on('blur change', '#pledge_pay_body .pledge-select-wrap > select', function () {
    $(this).closest('.pledge-select-wrap').removeClass('pledge-select-open');
});

$(document).on('change', '#pledge_pay_body #pledge_payment_duration', function () {
    calculateRecurringPaymentAmount($(this));
});

$(document).on('change', '#pledge_pay_body #cover_surcharge', function () {
    calculateRecurringPaymentAmount($(this));
});

$(document).on('change', '#PledgeEditOngoingModal #on_num_left', function () {
    const $modal = $('#PledgeEditOngoingModal');
    const minTransAmt = Number($modal.data('editOngoingMinTransAmt')) || 0;
    if ($modal.data('editRecurringApplyingMode')) {
        return;
    }
    if (isFinitePledgeRecurringEdit($modal)) {
        applySimpleRecurringPlan($modal, minTransAmt);
        updateEditRecurringModePanel($modal, minTransAmt);
        syncInstallmentLastPaymentNote($modal);
        return;
    }
    updateRecurringInstallmentAmount($modal, $modal.data('ongoingType'));
    updateEditRecurringModePanel($modal, minTransAmt);
    syncInstallmentWidget($modal);
});

$(document).on('input change', '#PledgeEditOngoingModal #edit_recurring_count_input', function () {
    const $modal = $(this).closest('.modal');
    applyCountInputFromField($modal);
});

$(document).on('input change', '#PledgeEditOngoingModal #edit_recurring_remaining_input', function () {
    const $modal = $(this).closest('.modal');
    applyRemainingInputFromField($modal);
    $('#PledgeEditOngoingModal #pledge_amount_input').trigger('input');
});

$(document).on('input change', '#PledgeEditOngoingModal #pledge_amount_input', function () {
    const $modal = $(this).closest('.modal');
    syncPledgeBalanceDueDisplay($modal);
    const minTransAmt = Number($modal.data('editOngoingMinTransAmt')) || 0;
    updateEditRecurringModePanel($modal, minTransAmt);
    if (!validatePledgeAmountInput($modal)) {
        return;
    }
    if (isFinitePledgeRecurringEdit($modal)) {
        applySimpleRecurringPlan($modal, minTransAmt);
        syncInstallmentLastPaymentNote($modal);
        return;
    }
    updateRecurringInstallmentAmount($modal);
    syncInstallmentWidget($modal);
});

$(document).on('input', '#PledgeEditOngoingModal #on_amount', function () {
    const $modal = $(this).closest('.modal');
    syncInstallmentLastPaymentNote($modal);
});

$(document).on('change', '#PledgeEditOngoingModal #on_amount', function () {
    const $modal = $(this).closest('.modal');
    if ($modal.data('editRecurringApplyingMode')) {
        return;
    }
    if (isFinitePledgeRecurringEdit($modal)) {
        syncInstallmentWidget($modal);
        syncInstallmentLastPaymentNote($modal);
        return;
    }
    syncInstallmentWidget($modal);
});

export {
    setPledgeStartDateByFrequency,
    setRecurringNoteVisibility,
    calculateRecurringPaymentAmount,
    syncRecurringInstallmentFields,
    validatePledgeAmountInput,
    initializeRecurringTable,
    destroyRecurringTable,
    setPledgeTotals,
    setInstallmentAmountNote,
    updateOngoingDurationOption,
    updateRecurringInstallmentAmount,
    closeEditOngoingPledgeModal,
};





